import { DoctorAvatar } from "./DoctorAvatar";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, X, User, FlaskConical, Package, BookOpen, ChevronRight, Loader2, Heart, Pill, Stethoscope } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function OmniSearchModal({ isOpen, onClose, onSelectDoctor, onSelectTest }) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ doctors: [], tests: [], packages: [], articles: [] });
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setQuery("");
      setResults({ doctors: [], tests: [], packages: [], articles: [] });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (isOpen) onClose();
        else {
          // Trigger open via window event or prop
        }
      }
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults({ doctors: [], tests: [], packages: [], articles: [] });
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`);
        setResults(res.data);
      } catch (err) {
        console.error("Search error:", err);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [query]);

  if (!isOpen) return null;

  const totalResults = results.doctors.length + results.tests.length + results.packages.length + results.articles.length + (results.medicines || []).length + (results.procedures || []).length + (results.physio_services || []).length;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 sm:pt-24 px-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
      <div 
        data-testid="omni-search-dialog"
        className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden"
      >
        {/* Search Header Input */}
        <div className="relative flex items-center px-6 py-4 border-b border-gray-100 bg-[#FBF9F4]">
          <Search className="w-5 h-5 text-[#3B8271] shrink-0 mr-3" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by doctor, specialty, test (e.g. 2D Echo, CBC, ECG), package, or symptoms..."
            data-testid="omni-search-input"
            className="w-full bg-transparent text-sm sm:text-base text-gray-900 placeholder:text-gray-400 focus:outline-none"
          />
          {loading && <Loader2 className="w-4 h-4 text-[#3B8271] animate-spin shrink-0 mr-2" />}
          <button 
            onClick={onClose}
            data-testid="omni-search-close-button"
            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Quick Suggestion Chips when empty */}
        {(!query || query.length < 2) && (
          <div className="p-6 bg-white space-y-4 text-xs">
            <p className="text-gray-400 font-medium uppercase tracking-wider text-[10px]">Popular Searches at 6SigmaCare Kolkata</p>
            <div className="flex flex-wrap gap-2">
              {["Cardiologist", "2D Echo", "HbA1c", "Lipid Profile", "Thyroid Profile", "Digital X-Ray Chest", "Ultrasound Abdomen", "Full Body Health Check", "Dr. Subhashish Mukherjee"].map((term) => (
                <button
                  key={term}
                  onClick={() => setQuery(term)}
                  data-testid={`search-tag-${term.toLowerCase().replace(/\s+/g, '-')}`}
                  className="px-3 py-1.5 bg-[#EBF4F1] hover:bg-emerald-100 text-[#0D3B2E] rounded-full font-medium transition-colors"
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search Results Display */}
        {query && query.length >= 2 && (
          <div className="max-h-[60vh] overflow-y-auto p-4 sm:p-6 space-y-6">
            {totalResults === 0 && !loading && (
              <div className="text-center py-8 text-gray-500 text-xs">
                <p className="font-semibold text-gray-700 mb-1">No direct matching results for "{query}"</p>
                <p>Try searching for a test category like "Blood", "Pathology", "Echo", "X-Ray", or consult our specialists.</p>
              </div>
            )}

            {/* Doctors Results */}
            {results.doctors.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#0D3B2E] flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-[#3B8271]" /> Specialist Doctors ({results.doctors.length})
                </h4>
                <div className="space-y-1.5">
                  {results.doctors.map((doc) => (
                    <div
                      key={doc.id}
                      onClick={() => {
                        onClose();
                        if (onSelectDoctor) onSelectDoctor(doc);
                        else navigate(`/doctors?select=${doc.id}`);
                      }}
                      data-testid={`search-result-doc-${doc.id}`}
                      className="p-3 bg-[#FBF9F4] hover:bg-[#EBF4F1] rounded-2xl cursor-pointer flex items-center justify-between transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <DoctorAvatar doctor={doc} className="w-10 h-10 rounded-full border border-emerald-300/40" textClass="text-xs" />
                        <div>
                          <p className="text-xs font-bold text-gray-900 group-hover:text-[#0D3B2E]">{doc.name}</p>
                          <p className="text-[11px] text-[#3B8271] font-medium">{doc.specialty} • {doc.qualification}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-bold text-[#0D3B2E]">₹{doc.physical_fee}</span>
                        <ChevronRight className="w-4 h-4 text-gray-400 inline ml-1 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Diagnostic Tests Results */}
            {results.tests.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#0D3B2E] flex items-center gap-1.5">
                  <FlaskConical className="w-3.5 h-3.5 text-[#3B8271]" /> Diagnostic Investigations ({results.tests.length})
                </h4>
                <div className="space-y-1.5">
                  {results.tests.map((test) => (
                    <div
                      key={test.id}
                      onClick={() => {
                        onClose();
                        if (onSelectTest) onSelectTest(test);
                        else navigate(`/diagnostics?test=${test.id}`);
                      }}
                      data-testid={`search-result-test-${test.id}`}
                      className="p-3 bg-[#FBF9F4] hover:bg-[#EBF4F1] rounded-2xl cursor-pointer flex items-center justify-between transition-colors group"
                    >
                      <div>
                        <p className="text-xs font-bold text-gray-900 group-hover:text-[#0D3B2E]">{test.name}</p>
                        <p className="text-[11px] text-gray-500">{test.category} • {test.tat} {test.fasting_required ? "• 10h Fasting Required" : ""}</p>
                      </div>
                      <div className="text-right flex items-center gap-2">
                        <span className="text-[10px] text-gray-400 line-through">₹{test.mrp}</span>
                        <span className="text-xs font-bold text-emerald-800">₹{test.price}</span>
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Health Packages Results */}
            {results.packages.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#0D3B2E] flex items-center gap-1.5">
                  <Package className="w-3.5 h-3.5 text-amber-600" /> Health Packages ({results.packages.length})
                </h4>
                <div className="space-y-1.5">
                  {results.packages.map((pkg) => (
                    <div
                      key={pkg.id}
                      onClick={() => {
                        onClose();
                        navigate(`/packages#${pkg.slug}`);
                      }}
                      data-testid={`search-result-pkg-${pkg.id}`}
                      className="p-3 bg-amber-50/50 hover:bg-amber-100/60 rounded-2xl cursor-pointer flex items-center justify-between transition-colors group border border-amber-200/40"
                    >
                      <div>
                        <p className="text-xs font-bold text-gray-900">{pkg.name}</p>
                        <p className="text-[11px] text-amber-800">{pkg.tests_count} Tests Included • {pkg.category}</p>
                      </div>
                      <div className="text-right flex items-center gap-2">
                        <span className="text-[10px] text-gray-400 line-through">₹{pkg.mrp}</span>
                        <span className="text-xs font-bold text-[#0D3B2E]">₹{pkg.price}</span>
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Health Articles Results */}
            {results.articles.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#0D3B2E] flex items-center gap-1.5">
                  <BookOpen className="w-3.5 h-3.5 text-sky-600" /> Health Library Articles ({results.articles.length})
                </h4>
                <div className="space-y-1.5">
                  {results.articles.map((art) => (
                    <div
                      key={art.id}
                      onClick={() => {
                        onClose();
                        navigate(`/health-library#${art.slug}`);
                      }}
                      className="p-3 bg-sky-50/40 hover:bg-sky-100/50 rounded-2xl cursor-pointer flex items-center justify-between transition-colors group"
                    >
                      <div>
                        <p className="text-xs font-bold text-gray-900">{art.title}</p>
                        <p className="text-[11px] text-gray-500">By {art.author} • {art.read_time_mins} min read</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(results.medicines || []).length > 0 && (
              <div className="space-y-2" data-testid="omni-medicines-group">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#0D3B2E] flex items-center gap-1.5">
                  <Pill className="w-3.5 h-3.5 text-emerald-600" /> Medicines & Health Products ({results.medicines.length})
                </h4>
                <div className="space-y-1.5">
                  {results.medicines.map((m) => (
                    <div key={m.id} onClick={() => { onClose(); navigate(`/medicines/product/${m.slug}`); }} data-testid={`omni-medicine-${m.id}`} className="p-3 bg-emerald-50/40 hover:bg-emerald-100/50 rounded-2xl cursor-pointer flex items-center justify-between transition-colors group">
                      <div>
                        <p className="text-xs font-bold text-gray-900">{m.name} {m.prescription_required && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-bold">Rx</span>}</p>
                        <p className="text-[11px] text-gray-500">{m.pack_size} • ₹{m.selling_price}{m.mrp > m.selling_price ? <span className="line-through ml-1">₹{m.mrp}</span> : null} • {m.stock_qty > 0 ? "In stock" : "Out of stock"}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(results.procedures || []).length > 0 && (
              <div className="space-y-2" data-testid="omni-procedures-group">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#0D3B2E] flex items-center gap-1.5"><Stethoscope className="w-3.5 h-3.5 text-emerald-600" /> Minor Procedures ({results.procedures.length})</h4>
                <div className="space-y-1.5">{results.procedures.map((p) => (
                  <div key={p.id} onClick={() => { onClose(); navigate(`/procedures/${p.id}`); }} data-testid={`omni-procedure-${p.id}`} className="p-3 bg-emerald-50/40 hover:bg-emerald-100/50 rounded-2xl cursor-pointer flex items-center justify-between transition-colors group">
                    <div><p className="text-xs font-bold text-gray-900">{p.name}</p><p className="text-[11px] text-gray-500">Treatment room · ~{p.duration_min} min{p.selling_price != null ? ` • ₹${p.selling_price}` : ""} • clinician review</p></div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
                  </div>))}</div>
              </div>
            )}
            {(results.physio_services || []).length > 0 && (
              <div className="space-y-2" data-testid="omni-physio-group">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#0D3B2E] flex items-center gap-1.5"><Stethoscope className="w-3.5 h-3.5 text-emerald-600" /> Physiotherapy ({results.physio_services.length})</h4>
                <div className="space-y-1.5">{results.physio_services.map((p) => (
                  <div key={p.id} onClick={() => { onClose(); navigate(`/physiotherapy`); }} data-testid={`omni-physio-${p.id}`} className="p-3 bg-emerald-50/40 hover:bg-emerald-100/50 rounded-2xl cursor-pointer flex items-center justify-between transition-colors group">
                    <div><p className="text-xs font-bold text-gray-900">{p.name}</p><p className="text-[11px] text-gray-500">Physiotherapy{p.duration_min ? ` · ${p.duration_min} min` : ""}{p.fee != null ? ` • ₹${p.fee}` : ""}</p></div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
                  </div>))}</div>
              </div>
            )}
          </div>
        )}

        {/* Footer info */}
        <div className="p-3 bg-gray-50 border-t border-gray-100 text-[11px] text-gray-500 flex justify-between items-center px-6">
          <span>Press <b>ESC</b> to close</span>
          <span className="text-[#3B8271] font-medium">6SigmaCare Advanced Search</span>
        </div>
      </div>
    </div>
  );
}
